import { useCallback, useEffect, useRef, useState } from 'react'

export type AutoSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

interface Options<T> {
  initial: T
  onSave: (state: T) => Promise<void>
  debounceMs?: number
}

export function useAutoSaveForm<T extends object>({
  initial,
  onSave,
  debounceMs = 800,
}: Options<T>) {
  const [draft, setDraft] = useState<T>(initial)
  const [savedSnapshot, setSavedSnapshot] = useState<T>(initial)
  const [status, setStatus] = useState<AutoSaveStatus>('idle')
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave

  const flushSave = useCallback(async (state: T) => {
    setStatus('saving')
    setError(null)
    try {
      await onSaveRef.current(state)
      setSavedSnapshot(state)
      setSavedAt(new Date())
      setStatus('saved')
    } catch (e) {
      setError((e as Error).message || 'Kaydedilemedi')
      setStatus('error')
    }
  }, [])

  // draft değişince debounce save tetikle
  useEffect(() => {
    if (JSON.stringify(draft) === JSON.stringify(savedSnapshot)) {
      return
    }
    setStatus('dirty')
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      flushSave(draft)
    }, debounceMs)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [draft, savedSnapshot, debounceMs, flushSave])

  const patch = useCallback((partial: Partial<T>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
  }, [])

  const undo = useCallback(() => {
    setDraft(savedSnapshot)
    setStatus('saved')
    setError(null)
  }, [savedSnapshot])

  const canUndo = JSON.stringify(draft) !== JSON.stringify(savedSnapshot)

  // External update (örn server-side change push)
  const reset = useCallback((next: T) => {
    setDraft(next)
    setSavedSnapshot(next)
    setStatus('idle')
  }, [])

  return { draft, setDraft, patch, status, savedAt, error, canUndo, undo, reset }
}
